import Plugin from '../core/Plugin';
import * as SelectionUtils from '../SelectionUtils';

/**
 * SymbolPlugin - Provides insertion of special characters and symbols
 * Supports mathematical symbols, currency, arrows, greek letters, and more
 */
class SymbolPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'Symbol';

    // Comprehensive symbol categories
    this.symbolCategories = {
      Mathematical: [
        { symbol: '±', name: 'Plus-Minus' },
        { symbol: '×', name: 'Multiplication' },
        { symbol: '÷', name: 'Division' },
        { symbol: '≈', name: 'Approximately' },
        { symbol: '≠', name: 'Not Equal' },
        { symbol: '≤', name: 'Less Than or Equal' },
        { symbol: '≥', name: 'Greater Than or Equal' },
        { symbol: '∞', name: 'Infinity' },
        { symbol: '√', name: 'Square Root' },
        { symbol: '∑', name: 'Summation' },
        { symbol: '∏', name: 'Product' },
        { symbol: '∫', name: 'Integral' },
        { symbol: '∂', name: 'Partial Derivative' },
        { symbol: '∇', name: 'Nabla' },
        { symbol: '∆', name: 'Delta' },
        { symbol: '∈', name: 'Element Of' },
        { symbol: '∉', name: 'Not Element Of' },
        { symbol: '∋', name: 'Contains' },
        { symbol: '⊂', name: 'Subset' },
        { symbol: '⊃', name: 'Superset' },
        { symbol: '∩', name: 'Intersection' },
        { symbol: '∪', name: 'Union' },
        { symbol: '∀', name: 'For All' },
        { symbol: '∃', name: 'There Exists' },
        { symbol: '∅', name: 'Empty Set' },
        { symbol: '⊥', name: 'Perpendicular' },
        { symbol: '∥', name: 'Parallel' },
        { symbol: '∠', name: 'Angle' },
        { symbol: '°', name: 'Degree' },
        { symbol: '′', name: 'Prime' },
        { symbol: '″', name: 'Double Prime' },
      ],
      'Greek Letters': [
        { symbol: 'α', name: 'Alpha' },
        { symbol: 'β', name: 'Beta' },
        { symbol: 'γ', name: 'Gamma' },
        { symbol: 'δ', name: 'Delta' },
        { symbol: 'ε', name: 'Epsilon' },
        { symbol: 'ζ', name: 'Zeta' },
        { symbol: 'η', name: 'Eta' },
        { symbol: 'θ', name: 'Theta' },
        { symbol: 'ι', name: 'Iota' },
        { symbol: 'κ', name: 'Kappa' },
        { symbol: 'λ', name: 'Lambda' },
        { symbol: 'μ', name: 'Mu' },
        { symbol: 'ν', name: 'Nu' },
        { symbol: 'ξ', name: 'Xi' },
        { symbol: 'ο', name: 'Omicron' },
        { symbol: 'π', name: 'Pi' },
        { symbol: 'ρ', name: 'Rho' },
        { symbol: 'σ', name: 'Sigma' },
        { symbol: 'τ', name: 'Tau' },
        { symbol: 'υ', name: 'Upsilon' },
        { symbol: 'φ', name: 'Phi' },
        { symbol: 'χ', name: 'Chi' },
        { symbol: 'ψ', name: 'Psi' },
        { symbol: 'ω', name: 'Omega' },
        { symbol: 'Α', name: 'Alpha (uppercase)' },
        { symbol: 'Β', name: 'Beta (uppercase)' },
        { symbol: 'Γ', name: 'Gamma (uppercase)' },
        { symbol: 'Δ', name: 'Delta (uppercase)' },
        { symbol: 'Θ', name: 'Theta (uppercase)' },
        { symbol: 'Λ', name: 'Lambda (uppercase)' },
        { symbol: 'Π', name: 'Pi (uppercase)' },
        { symbol: 'Σ', name: 'Sigma (uppercase)' },
        { symbol: 'Φ', name: 'Phi (uppercase)' },
        { symbol: 'Ψ', name: 'Psi (uppercase)' },
        { symbol: 'Ω', name: 'Omega (uppercase)' },
      ],
      Currency: [
        { symbol: '$', name: 'Dollar' },
        { symbol: '€', name: 'Euro' },
        { symbol: '£', name: 'Pound' },
        { symbol: '¥', name: 'Yen' },
        { symbol: '¢', name: 'Cent' },
        { symbol: '₹', name: 'Rupee' },
        { symbol: '₽', name: 'Ruble' },
        { symbol: '₩', name: 'Won' },
        { symbol: '₪', name: 'Shekel' },
        { symbol: '฿', name: 'Baht' },
        { symbol: '₫', name: 'Dong' },
        { symbol: '₱', name: 'Peso' },
      ],
      Arrows: [
        { symbol: '←', name: 'Left Arrow' },
        { symbol: '→', name: 'Right Arrow' },
        { symbol: '↑', name: 'Up Arrow' },
        { symbol: '↓', name: 'Down Arrow' },
        { symbol: '↔', name: 'Left-Right Arrow' },
        { symbol: '↕', name: 'Up-Down Arrow' },
        { symbol: '↖', name: 'North-West Arrow' },
        { symbol: '↗', name: 'North-East Arrow' },
        { symbol: '↘', name: 'South-East Arrow' },
        { symbol: '↙', name: 'South-West Arrow' },
        { symbol: '⇐', name: 'Double Left Arrow' },
        { symbol: '⇒', name: 'Double Right Arrow' },
        { symbol: '⇑', name: 'Double Up Arrow' },
        { symbol: '⇓', name: 'Double Down Arrow' },
        { symbol: '⇔', name: 'Double Left-Right Arrow' },
        { symbol: '⇕', name: 'Double Up-Down Arrow' },
        { symbol: '↩', name: 'Return Arrow' },
        { symbol: '↪', name: 'Return Arrow Right' },
        { symbol: '⤴', name: 'Arrow Up Right' },
        { symbol: '⤵', name: 'Arrow Down Right' },
      ],
      Punctuation: [
        { symbol: '…', name: 'Ellipsis' },
        { symbol: '–', name: 'En Dash' },
        { symbol: '—', name: 'Em Dash' },
        { symbol: '‐', name: 'Hyphen' },
        { symbol: '‒', name: 'Figure Dash' },
        { symbol: '\u2018', name: 'Left Single Quote' },
        { symbol: '\u2019', name: 'Right Single Quote' },
        { symbol: '\u201C', name: 'Left Double Quote' },
        { symbol: '\u201D', name: 'Right Double Quote' },
        { symbol: '«', name: 'Left Guillemet' },
        { symbol: '»', name: 'Right Guillemet' },
        { symbol: '‹', name: 'Single Left Guillemet' },
        { symbol: '›', name: 'Single Right Guillemet' },
        { symbol: '¿', name: 'Inverted Question Mark' },
        { symbol: '¡', name: 'Inverted Exclamation' },
        { symbol: '§', name: 'Section Sign' },
        { symbol: '¶', name: 'Pilcrow' },
        { symbol: '†', name: 'Dagger' },
        { symbol: '‡', name: 'Double Dagger' },
        { symbol: '•', name: 'Bullet' },
        { symbol: '‣', name: 'Triangular Bullet' },
      ],
      'Common Symbols': [
        { symbol: '©', name: 'Copyright' },
        { symbol: '®', name: 'Registered' },
        { symbol: '™', name: 'Trademark' },
        { symbol: '℠', name: 'Service Mark' },
        { symbol: '№', name: 'Number Sign' },
        { symbol: '℃', name: 'Celsius' },
        { symbol: '℉', name: 'Fahrenheit' },
        { symbol: 'Ω', name: 'Ohm' },
        { symbol: 'Å', name: 'Angstrom' },
        { symbol: 'µ', name: 'Micro' },
        { symbol: '‰', name: 'Per Mille' },
        { symbol: '‱', name: 'Per Ten Thousand' },
        { symbol: '¼', name: 'One Quarter' },
        { symbol: '½', name: 'One Half' },
        { symbol: '¾', name: 'Three Quarters' },
        { symbol: '⅓', name: 'One Third' },
        { symbol: '⅔', name: 'Two Thirds' },
        { symbol: '⅛', name: 'One Eighth' },
        { symbol: '⅜', name: 'Three Eighths' },
        { symbol: '⅝', name: 'Five Eighths' },
        { symbol: '⅞', name: 'Seven Eighths' },
      ],
      'Shapes & Symbols': [
        { symbol: '○', name: 'White Circle' },
        { symbol: '●', name: 'Black Circle' },
        { symbol: '◯', name: 'Large Circle' },
        { symbol: '□', name: 'White Square' },
        { symbol: '■', name: 'Black Square' },
        { symbol: '△', name: 'White Triangle' },
        { symbol: '▲', name: 'Black Triangle' },
        { symbol: '▽', name: 'White Down Triangle' },
        { symbol: '▼', name: 'Black Down Triangle' },
        { symbol: '◇', name: 'White Diamond' },
        { symbol: '◆', name: 'Black Diamond' },
        { symbol: '★', name: 'Black Star' },
        { symbol: '☆', name: 'White Star' },
        { symbol: '♠', name: 'Spade' },
        { symbol: '♣', name: 'Club' },
        { symbol: '♥', name: 'Heart' },
        { symbol: '♦', name: 'Diamond' },
        { symbol: '✓', name: 'Check Mark' },
        { symbol: '✗', name: 'X Mark' },
        { symbol: '✘', name: 'Heavy X Mark' },
        { symbol: '☐', name: 'Ballot Box' },
        { symbol: '☑', name: 'Ballot Box with Check' },
        { symbol: '☒', name: 'Ballot Box with X' },
      ],
    };
  }

  executeCommand(command) {
    if (command === 'insertSymbol' || command === 'symbol') {
      this.showSymbolPicker();
    }
  }

  showSymbolPicker() {
    // Save the current selection before showing modal
    const savedSelection = SelectionUtils.saveSelection();

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'quillkit-dialog-overlay';

    // Create dialog
    const dialog = document.createElement('div');
    dialog.className = 'quillkit-dialog quillkit-symbol-picker-dialog';
    dialog.style.maxWidth = '700px';
    dialog.style.width = '90%';

    // Create header
    const header = document.createElement('div');
    header.className = 'quillkit-dialog-header';
    const title = document.createElement('h2');
    title.textContent = 'Insert Special Character';
    title.style.margin = '0';
    header.appendChild(title);

    // Create body
    const body = document.createElement('div');
    body.className = 'quillkit-dialog-body';
    body.style.maxHeight = '500px';
    body.style.overflowY = 'auto';

    // Create category tabs
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'quillkit-symbol-tabs';
    tabsContainer.style.display = 'flex';
    tabsContainer.style.flexWrap = 'wrap';
    tabsContainer.style.gap = '8px';
    tabsContainer.style.marginBottom = '15px';
    tabsContainer.style.borderBottom = '1px solid #ddd';
    tabsContainer.style.paddingBottom = '10px';

    // Create content area
    const contentArea = document.createElement('div');
    contentArea.className = 'quillkit-symbol-content';

    // Function to render symbols for a category
    const renderCategory = (categoryName) => {
      contentArea.innerHTML = '';
      const symbols = this.symbolCategories[categoryName];

      const grid = document.createElement('div');
      grid.className = 'quillkit-symbol-grid';
      grid.style.display = 'grid';
      grid.style.gridTemplateColumns = 'repeat(auto-fill, minmax(60px, 1fr))';
      grid.style.gap = '8px';
      grid.style.padding = '10px 0';

      symbols.forEach(({ symbol, name }) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'quillkit-symbol-button';
        btn.title = name;
        btn.textContent = symbol;
        btn.style.fontSize = '24px';
        btn.style.padding = '12px';
        btn.style.border = '1px solid #ddd';
        btn.style.borderRadius = '4px';
        btn.style.background = '#fff';
        btn.style.cursor = 'pointer';
        btn.style.transition = 'all 0.2s';
        btn.style.display = 'flex';
        btn.style.alignItems = 'center';
        btn.style.justifyContent = 'center';
        btn.style.minHeight = '50px';
        btn.style.color = '#000'; // Set default visible color for all buttons

        btn.addEventListener('mouseenter', () => {
          btn.style.background = '#e3f2fd';
          btn.style.borderColor = '#2196f3';
          btn.style.transform = 'scale(1.1)';
        });

        btn.addEventListener('mouseleave', () => {
          btn.style.background = '#fff';
          btn.style.borderColor = '#ddd';
          btn.style.transform = 'scale(1)';
        });

        btn.addEventListener('click', () => {
          // Focus editor first if richEditor exists
          if (this.editor.richEditor) {
            this.editor.richEditor.focus({ preventScroll: true });

            // Restore selection
            if (savedSelection) {
              try {
                SelectionUtils.restoreSelection(savedSelection);
              } catch (e) {
                // If restore fails, create a selection at the end
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(this.editor.richEditor);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
              }
            }
          }

          // Insert the symbol
          SelectionUtils.insertHTML(symbol);

          // Sync and save
          if (this.editor.syncToTextarea) {
            this.editor.syncToTextarea();
          }
          if (this.editor.saveToHistory) {
            this.editor.saveToHistory();
          }

          closeModal();
        });

        grid.appendChild(btn);
      });

      contentArea.appendChild(grid);
    };

    // Create tabs for each category
    const categoryNames = Object.keys(this.symbolCategories);
    categoryNames.forEach((categoryName, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'quillkit-symbol-tab';
      tab.textContent = categoryName;
      tab.style.padding = '8px 16px';
      tab.style.border = 'none';
      tab.style.background = 'transparent';
      tab.style.cursor = 'pointer';
      tab.style.borderBottom = '2px solid transparent';
      tab.style.transition = 'all 0.2s';
      tab.style.fontSize = '14px';
      tab.style.color = '#000'; // Set default visible color for all tabs

      if (index === 0) {
        tab.style.borderBottomColor = '#2196f3';
        tab.style.color = '#2196f3';
        tab.style.fontWeight = 'bold';
      }

      tab.addEventListener('click', () => {
        // Update active tab styling
        tabsContainer.querySelectorAll('.quillkit-symbol-tab').forEach((t) => {
          t.style.borderBottomColor = 'transparent';
          t.style.color = '#000'; // Ensure inactive tabs are visible
          t.style.fontWeight = 'normal';
        });
        tab.style.borderBottomColor = '#2196f3';
        tab.style.color = '#2196f3';
        tab.style.fontWeight = 'bold';

        // Render category
        renderCategory(categoryName);
      });

      tabsContainer.appendChild(tab);
    });

    // Render first category by default
    renderCategory(categoryNames[0]);

    body.appendChild(tabsContainer);
    body.appendChild(contentArea);

    // Create footer
    const footer = document.createElement('div');
    footer.className = 'quillkit-dialog-footer';

    const closeModal = () => {
      if (overlay && overlay.parentNode === document.body) {
        document.body.removeChild(overlay);
      }
    };

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'quillkit-editor-button';
    cancelBtn.type = 'button';
    cancelBtn.addEventListener('click', closeModal);

    footer.appendChild(cancelBtn);

    // Assemble dialog
    dialog.appendChild(header);
    dialog.appendChild(body);
    dialog.appendChild(footer);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Overlay click closes modal
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });

    // Escape key closes modal
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    });
  }

  getMetadata() {
    return {
      name: 'Special Characters & Symbols',
      version: '1.0.0',
      description:
        'Provides insertion of special characters and symbols including mathematical symbols, Greek letters, currency, arrows, and more',
      author: 'AtlaraFirm',
    };
  }
}

export default SymbolPlugin;
